import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ChordProParser, HtmlFormatter, FormatterSettings } from "chordproject-parser"
import { parse } from 'chordjs'

import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material'

import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove'

import { useSelector, useDispatch } from 'react-redux'
import { deleteSong } from '../actions/songActions'
import { removeSongFromPlaylist } from '../actions/playlistActions'

const Formatter = ({ song }) => {
  const [songHtml, setSongHtml] = useState('')
  const [songData, setSongData] = useState({})
  const [showChords, setShowChords] = useState(true)
  const [textSize, setTextSize] = useState(1)

  const { playlistId } = useParams()
  const { userInfo } = useSelector(state => state.userState)
  const { playlist } = useSelector(state => state.playlistState)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  useEffect(() => {
    const parser = new ChordProParser()
    const parsedSong = parser.parse(song.body)
    setSongData(parsedSong)

    const settings = new FormatterSettings()
    if (!showChords) {
      settings.showChords = false
    }

    const formatter = new HtmlFormatter(settings)
    const songText = formatter.format(parsedSong)
    setSongHtml(songText.join(''))
  }, [showChords, song])

  useEffect(() => {
    if (!showChords) {
      const lines = document.querySelectorAll('.lyrics-line')
      lines.forEach(line => {
        let newLine = ''
        const words = line.querySelectorAll('.word')
        if (words.length > 0) {
          words.forEach(word => {
            let newWord = ''
            const syllables = word.querySelectorAll('.lyrics')

            syllables.forEach(syllable => {
              newWord += syllable.innerText
            })

            newWord += ' '
            newLine += newWord
          })

          line.innerHTML = newLine
        }
      })
    }
    const emptyLines = document.querySelectorAll('.empty-line')
    emptyLines.forEach(emptyLine => {
      emptyLine.innerText = '\n'
    })
  }, [songHtml, showChords])

  useEffect(() => {
    const chords = document.querySelectorAll('.chord')

    chords.forEach(chord => {
      if (songData.capo) {
        const parsedChord = parse(chord.innerText)
        const chord2 = parsedChord.transpose(songData.capo)

        chord.innerText = chord2.toString()
      }
    })
  }, [songHtml, showChords])

  const transposeChordUp = () => {
    document.querySelectorAll('.chord').forEach(c => {
      const chord = parse(c.innerText);
      if (chord !== null) {
        c.innerText = chord.transposeUp().toString();
      }
    })
  }

  const transposeChordDown = () => {
    document.querySelectorAll('.chord').forEach(c => {
      const chord = parse(c.innerText);
      if (chord !== null) {
        c.innerText = chord.transposeDown().toString();
      }
    })
  }

  return (
    <Box sx={{ height: '93vh', overflow: 'auto', pt: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 3, width: '100%' }}>
        <Typography variant='h4'>{song.title}</Typography>
        <Box>
          <Tooltip title={showChords ? 'Hide chords' : 'Show chords'}>
            <IconButton onClick={() => setShowChords(!showChords)}>
              {showChords ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title='Transpose up'>
            <IconButton onClick={transposeChordUp}>
              <ArrowUpwardIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title='Transpose down'>
            <IconButton onClick={transposeChordDown}>
              <ArrowDownwardIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title='Zoom in'>
            <IconButton onClick={() => setTextSize(textSize + 0.25)}>
              <AddIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title='Zoom out'>
            <IconButton onClick={() => setTextSize(textSize - 0.25)}>
              <RemoveIcon />
            </IconButton>
          </Tooltip>

          {userInfo && <Link style={{ color: 'inherit', textDecoration: 'none' }} to={`/playlist/add/${song._id}`}>
            <Tooltip title='Add to playlist'>
              <IconButton>
                <PlaylistAddIcon />
              </IconButton>
            </Tooltip>
          </Link>}
          {playlistId && playlist && (userInfo && userInfo !== null && userInfo?.role === 'admin' || userInfo?.role === 'superadmin' || userInfo?._id === playlist.user._id) && <Tooltip title='Remove song from playlist'>
            <IconButton onClick={() => dispatch(removeSongFromPlaylist(playlistId, song._id, navigate))}>
              <PlaylistRemoveIcon />
            </IconButton>
          </Tooltip>}
          {userInfo && userInfo !== null && (userInfo?.role === 'admin' || userInfo?.role === 'superadmin') && <>
            <Link to={`/edit/song/${song._id}`}>
              <Tooltip title='Edit song'>
                <IconButton>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Link>
            <Tooltip title='Delete song'>
              <IconButton onClick={() => dispatch(deleteSong(song._id, navigate))}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </>}

        </Box>
      </Box>
      {song.link && <Box sx={{ my: 2 }}>
        <a style={{ color: '#1366f2' }} href={song.link}>Click here to visit song</a>
      </Box>}
      <div id='song' style={{ fontSize: `17` * textSize + 'px' }} dangerouslySetInnerHTML={{ __html: songHtml }}></div>
    </Box>
  )
}

export default Formatter